import { useAppTheme } from "../../../hooks/useAppTheme";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  Modal as RNModal,
  Pressable,
  View as RNView,
  ActivityIndicator,
} from "react-native";
import { View } from "../../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import { DateInputControl } from "../../../components/custom/DateInputControl";
import Spinner from "../../../components/shared/Spinner";
import {
  getIntouchSchedule,
  getIntouchMachines,
  getIntouchFinishedJobs,
  previewIntouchImport,
  confirmIntouchImport,
  IntouchScheduleRow,
  IntouchMachine,
  IntouchPreviewRow,
} from "../../../services/intouchService";
import { useToast } from "react-native-toast-notifications";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  headers,
  nestedHeaders,
  SmallDeviceNestedHeaders,
  finishedHeaders,
  SmallDeviceFinishedHeaders,
  finishedNestedHeaders,
  SmallDeviceFinishedNestedHeaders,
} from "./utils/intouchScheduleHeaders";
import { useTranslation } from "../../../utils/useTranslation";
import { handleTableLoader } from "../../../utils/common";
import { sortColumns } from "../../../utils/sortColumns";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import { subtractDaysFromDate } from "../../../utils/dateUtility";
import { registerTranslation } from "react-native-paper-dates";
// @ts-ignore
import { Feather } from "@expo/vector-icons";

registerTranslation("en-AU", {
  save: "Save",
  selectSingle: "Select date",
  selectMultiple: "Select dates",
  selectRange: "Select period",
  notAccordingToDateFormat: (inputFormat) =>
    `Date format must be ${inputFormat}`,
  mustBeHigherThan: (date) => `Must be later than ${date}`,
  mustBeLowerThan: (date) => `Must be earlier than ${date}`,
  mustBeBetween: (startDate, endDate) =>
    `Must be between ${startDate} - ${endDate}`,
  dateIsDisabled: "Day is not allowed",
  previous: "Previous",
  next: "Next",
  typeInDate: "Type in date",
  pickDateFromCalendar: "Pick date from calendar",
  close: "Close",
  hour: "Hour",
  minute: "Minute",
  startDate: "Start Date (DD/MM/YYYY)",
  endDate: "End Date (DD/MM/YYYY)",
  dateFormat: "DD/MM/YYYY",
});

// Intouch's job-history and production-profile endpoints slow down sharply as the
// window grows (~6s over 30 days, ~18s over 90), so the default stays modest and
// the user can widen it when they need to.
const DEFAULT_DAYS_BACK = 30;

const SUMMARY_STYLES = {
  success: { color: "#16A34A", icon: "check-circle" },
  warning: { color: "#D97706", icon: "alert-triangle" },
  error: { color: "#DC2626", icon: "x-circle" },
} as const;

type SummaryEntryType = keyof typeof SUMMARY_STYLES;

interface ImportSummaryEntry {
  type: SummaryEntryType;
  orderNumber: string | null;
  statusCode: number | null;
  statusMessage: string;
  description: string;
}

/**
 * Intouch reports each result as a line holding one or more status Items.
 * Flattens them into a single list the summary window can render directly.
 */
function flattenImportReport(report: any): Array<ImportSummaryEntry> {
  const sections: Array<[string, SummaryEntryType]> = [
    ["Errors", "error"],
    ["Warnings", "warning"],
    ["SuccessfullyImported", "success"],
  ];

  return sections.flatMap(([key, type]) =>
    (report?.[key] || []).flatMap((line: any) => {
      const items = line.Items && line.Items.length > 0 ? line.Items : [{}];
      return items.map((item: any) => ({
        type,
        orderNumber: line.orderNumber || null,
        statusCode: item.StatusCode ?? null,
        statusMessage: item.StatusMessage || (type === "success" ? "Imported" : ""),
        description: item.Description || "",
      }));
    })
  );
}

export default function IntouchScheduleReportScreen({ navigation }: any) {
  const { colors } = useAppTheme();
  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const { t } = useTranslation();
  const toast = useToast();

  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [scheduleRows, setScheduleRows] = useState<Array<IntouchScheduleRow>>([]);
  const [pageLevelList, setPageLevelList] = useState<Array<IntouchScheduleRow>>([]);
  const [totalJobs, setTotalJobs] = useState<number>(0);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(headers);
  const [expandAllRows, setExpandAllRows] = useState<boolean>(false);

  // Finished jobs modal — filters are chosen first, then the report is fetched.
  const [finishedModalOpen, setFinishedModalOpen] = useState<boolean>(false);
  const [machineListLoading, setMachineListLoading] = useState<boolean>(false);
  const [machineList, setMachineList] = useState<Array<IntouchMachine>>([]);
  const [selectedMachineIds, setSelectedMachineIds] = useState<Set<string>>(new Set());
  const [startDate, setStartDate] = useState<Date>(
    subtractDaysFromDate(new Date(), DEFAULT_DAYS_BACK)
  );
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [finishedLoading, setFinishedLoading] = useState<boolean>(false);
  const [finishedRows, setFinishedRows] = useState<Array<IntouchScheduleRow>>([]);
  const [finishedTotalJobs, setFinishedTotalJobs] = useState<number>(0);
  const [showFinishedResults, setShowFinishedResults] = useState<boolean>(false);
  const [finishedSearch, setFinishedSearch] = useState<string>("");

  // Import jobs — preview the SAP works orders first, then confirm the selection.
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const [importConfirmLoading, setImportConfirmLoading] = useState<boolean>(false);
  const [importPreviewOpen, setImportPreviewOpen] = useState<boolean>(false);
  const [importPreviewRows, setImportPreviewRows] = useState<Array<IntouchPreviewRow>>([]);
  const [selectedImportRows, setSelectedImportRows] = useState<Set<string>>(new Set());
  const [importSummaryOpen, setImportSummaryOpen] = useState<boolean>(false);
  const [importSummary, setImportSummary] = useState<Array<ImportSummaryEntry>>([]);
  const [importedCount, setImportedCount] = useState<number>(0);

  useEffect(() => {
    navigation.setOptions({ title: t("intouchSchedule") });
  }, [navigation, t]);

  const loadSchedule = async () => {
    setIsLoaded(false);
    try {
      const result = await getIntouchSchedule();
      const rows: Array<IntouchScheduleRow> = result?.data?.rows || [];
      const machineErrors = result?.data?.machineErrors || [];

      setScheduleRows(rows);
      setPageLevelList(rows);
      setTotalJobs(result?.data?.totalJobs || 0);

      if (machineErrors.length > 0) {
        toast.show(
          `${machineErrors.length} machine(s) could not be read from Intouch`,
          { type: "warning" }
        );
      }
    } catch (error) {
      toast.show(
        typeof error === "string" ? error : "Failed to load the Intouch schedule",
        { type: "danger" }
      );
    } finally {
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    loadSchedule();
  }, []);

  useEffect(() => {
    if (isRefresh) {
      loadSchedule();
      setRefresh(false);
    }
  }, [isRefresh]);

  // Searching matches on the nested jobs too, so typing a works order number
  // keeps the line/machine it sits on rather than filtering everything out.
  const handleSearch = (searchText: string) => {
    handleTableLoader(true, false, setIsSearched);
    if (!searchText || searchText.trim() === "") {
      setPageLevelList(scheduleRows);
    } else {
      setPageLevelList(
        scheduleRows.filter(
          (row) =>
            filterBasedOnAnyKey([row], searchText).length > 0 ||
            filterBasedOnAnyKey(row.jobs, searchText).length > 0
        )
      );
    }
    handleTableLoader(false, true, setIsSearched);
  };

  const onHandleSortColumn = (columnKey: string) => {
    setPageLevelList(
      sortColumns(pageLevelList, columnKey, tableHeaders, setTableHeaders) as Array<IntouchScheduleRow>
    );
  };

  const getDataToExport = (rows: Array<IntouchScheduleRow>) =>
    rows.flatMap((row) =>
      row.jobs.map((job) => ({
        machineName: row.machineName,
        machineDescription: row.machineDescription,
        ...job,
      }))
    );

  // Opening the modal fetches the machine list only — the report itself waits
  // until the user has picked their filters, so nothing heavy loads up front.
  const openFinishedModal = async () => {
    setFinishedModalOpen(true);
    setShowFinishedResults(false);
    setFinishedSearch("");
    if (machineList.length > 0) return;

    setMachineListLoading(true);
    try {
      const result = await getIntouchMachines();
      setMachineList(result?.data?.machines || []);
    } catch (error) {
      toast.show(
        typeof error === "string" ? error : "Failed to load the Intouch machine list",
        { type: "danger" }
      );
    } finally {
      setMachineListLoading(false);
    }
  };

  const toggleMachineSelection = (machineId: string) => {
    setSelectedMachineIds((previous) => {
      const updated = new Set(previous);
      if (updated.has(machineId)) {
        updated.delete(machineId);
      } else {
        updated.add(machineId);
      }
      return updated;
    });
  };

  const toggleSelectAllMachines = () => {
    const allSelected =
      machineList.length > 0 && machineList.every((m) => selectedMachineIds.has(m.machineId));
    setSelectedMachineIds(
      allSelected ? new Set() : new Set(machineList.map((m) => m.machineId))
    );
  };

  /**
   * Narrows the loaded report rather than re-querying Intouch, which has no
   * search of its own — the date range and machine picker decide what gets
   * fetched, this filters within it. Matching jobs are kept even when their
   * machine doesn't match, so searching a works order finds it wherever it ran.
   */
  const filteredFinishedRows = useMemo(() => {
    const search = finishedSearch.trim();
    if (!search) return finishedRows;

    return finishedRows
      .map((row) => {
        const machineMatches = filterBasedOnAnyKey([row], search).length > 0;
        const matchingJobs = filterBasedOnAnyKey(row.jobs, search);
        if (machineMatches) return row;
        return matchingJobs.length > 0 ? { ...row, jobs: matchingJobs } : null;
      })
      .filter(Boolean) as Array<IntouchScheduleRow>;
  }, [finishedRows, finishedSearch]);

  const filteredFinishedJobCount = filteredFinishedRows.reduce(
    (sum, row) => sum + row.jobs.length,
    0
  );

  const runFinishedReport = async () => {
    if (startDate >= endDate) {
      toast.show("Start date must be earlier than end date", { type: "danger" });
      return;
    }

    setFinishedLoading(true);
    try {
      const result = await getIntouchFinishedJobs({
        machineIds: [...selectedMachineIds],
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
      setFinishedRows(result?.data?.rows || []);
      setFinishedTotalJobs(result?.data?.totalJobs || 0);
      setFinishedSearch("");
      setShowFinishedResults(true);
    } catch (error) {
      toast.show(
        typeof error === "string" ? error : "Failed to load finished jobs from Intouch",
        { type: "danger" }
      );
    } finally {
      setFinishedLoading(false);
    }
  };

  const allMachinesSelected =
    machineList.length > 0 && machineList.every((m) => selectedMachineIds.has(m.machineId));

  const handleImportToIntouch = async () => {
    setImportLoading(true);
    try {
      const result = await previewIntouchImport();
      const rows: Array<IntouchPreviewRow> = result?.data?.rows || [];
      setImportPreviewRows(rows);
      // Orders already in Intouch stay selectable, but start unticked so they
      // aren't re-imported by accident.
      setSelectedImportRows(
        new Set(rows.filter((r) => !r.issue && !r.intouchStatus).map((r) => r.orderNumber))
      );
      setImportPreviewOpen(true);
    } catch (error) {
      toast.show(
        typeof error === "string" ? error : "Failed to fetch Intouch import preview",
        { type: "danger" }
      );
    } finally {
      setImportLoading(false);
    }
  };

  const toggleImportRowSelection = (orderNumber: string) => {
    setSelectedImportRows((prev) => {
      const updated = new Set(prev);
      if (updated.has(orderNumber)) {
        updated.delete(orderNumber);
      } else {
        updated.add(orderNumber);
      }
      return updated;
    });
  };

  const toggleSelectAllImportRows = () => {
    const mappableOrderNumbers = importPreviewRows.filter((r) => !r.issue).map((r) => r.orderNumber);
    const allSelected =
      mappableOrderNumbers.length > 0 &&
      mappableOrderNumbers.every((o) => selectedImportRows.has(o));
    setSelectedImportRows(allSelected ? new Set() : new Set(mappableOrderNumbers));
  };

  const handleConfirmImport = async () => {
    const rowsToImport = importPreviewRows.filter((r) => selectedImportRows.has(r.orderNumber));
    setImportConfirmLoading(true);
    try {
      const result = await confirmIntouchImport(rowsToImport);
      const report = result?.data?.report;

      setImportedCount(report?.SuccessfullyImported?.length || 0);
      setImportSummary(flattenImportReport(report));
      setImportPreviewOpen(false);
      setImportSummaryOpen(true);
      // The imported jobs land straight on the schedule this screen shows.
      loadSchedule();
    } catch (error) {
      toast.show(typeof error === "string" ? error : "Import to Intouch failed", { type: "danger" });
    } finally {
      setImportConfirmLoading(false);
    }
  };

  const importableRows = importPreviewRows.filter((r) => !r.issue);
  const alreadyInIntouchCount = importableRows.filter((r) => r.intouchStatus).length;
  const allImportableSelected =
    importableRows.length > 0 &&
    importableRows.every((r) => selectedImportRows.has(r.orderNumber));

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.surface }}
      contentContainerStyle={{ flexGrow: 1 }}
    >
      <View style={[styles.container, { backgroundColor: colors.surfaceSubtle }]}>
        <View style={[styles.containerContent, { width: "100%" }]}>
          {isLoaded ? (
            <View>
              <LabelBannerWithSearchBar
                titleName={`${t("intouchSchedule")}: ${totalJobs}`}
                handleSearch={handleSearch}
                setRefresh={setRefresh}
                dataToExport={getDataToExport(pageLevelList)}
                fileName="IntouchSchedule"
                sheetName="Intouch Schedule"
                preSearchControls={
                  <RNView style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <RNView style={styles.expandLinkRow}>
                      <TouchableOpacity onPress={() => setExpandAllRows(true)}>
                        <Text style={[styles.expandLink, { color: colors.primary }]}>
                          {t("expandAll")}
                        </Text>
                      </TouchableOpacity>
                      <Text style={[styles.expandLinkDivider, { color: colors.textMuted }]}>|</Text>
                      <TouchableOpacity onPress={() => setExpandAllRows(false)}>
                        <Text style={[styles.expandLink, { color: colors.primary }]}>
                          {t("collapseAll")}
                        </Text>
                      </TouchableOpacity>
                    </RNView>
                    <TouchableOpacity
                      style={[
                        styles.finishedButton,
                        {
                          backgroundColor: colors.surfaceSubtle,
                          borderColor: colors.border,
                          opacity: importLoading ? 0.6 : 1,
                        },
                      ]}
                      disabled={importLoading}
                      onPress={handleImportToIntouch}
                    >
                      {importLoading ? (
                        <ActivityIndicator size="small" color={colors.textSecondary} />
                      ) : (
                        <Feather name="upload-cloud" size={14} color={colors.textSecondary} />
                      )}
                      <Text style={[styles.finishedButtonText, { color: colors.textSecondary }]}>
                        {t("importJobs")}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.finishedButton, { backgroundColor: colors.surfaceSubtle, borderColor: colors.border }]}
                      onPress={openFinishedModal}
                    >
                      <Feather name="check-circle" size={14} color={colors.textSecondary} />
                      <Text style={[styles.finishedButtonText, { color: colors.textSecondary }]}>
                        {t("finishedJobs")}
                      </Text>
                    </TouchableOpacity>
                  </RNView>
                }
              />
              <DataTableWithPagination
                headers={tableHeaders}
                rows={pageLevelList}
                itemsPerPage={100}
                useModal={false}
                isRowsExpandable={true}
                rowKeyValue="machineId"
                nestedItemKeyValue="jobs"
                isToExpandAllRows={expandAllRows}
                nestedHeader={isSmallDevice ? SmallDeviceNestedHeaders : nestedHeaders}
                columnsToNotHaveThousandSeparator={["Works Order"]}
                isDetail={false}
                isEdit={false}
                isDelete={false}
                setModalVisible={() => {}}
                setSelected={() => {}}
                isDataLoading={isSearched}
                handleRowClick={() => {}}
                handleDeleteRow={() => {}}
                handleSortColumn={onHandleSortColumn}
              />
            </View>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </View>

      <RNModal
        transparent={true}
        visible={finishedModalOpen}
        animationType="fade"
        onRequestClose={() => setFinishedModalOpen(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setFinishedModalOpen(false)}>
          <Pressable
            style={[styles.modalCard, { backgroundColor: colors.surface, borderColor: colors.border }]}
            onPress={(e) => e.stopPropagation()}
          >
            <RNView style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                {t("finishedJobs")}
                {showFinishedResults
                  ? finishedSearch.trim()
                    ? `: ${filteredFinishedJobCount} of ${finishedTotalJobs}`
                    : `: ${finishedTotalJobs}`
                  : ""}
              </Text>
              <TouchableOpacity onPress={() => setFinishedModalOpen(false)}>
                <Feather name="x" size={18} color={colors.textSecondary} />
              </TouchableOpacity>
            </RNView>

            {showFinishedResults ? (
              <>
                <RNView style={styles.modalActionsRow}>
                  <RNView
                    style={[
                      styles.searchContainer,
                      { borderColor: colors.border, backgroundColor: colors.surfaceSubtle },
                    ]}
                  >
                    <Feather name="search" size={14} color={colors.textMuted} />
                    <TextInput
                      style={[styles.searchInput, { color: colors.text }]}
                      placeholder={t("search")}
                      placeholderTextColor={colors.textMuted}
                      value={finishedSearch}
                      onChangeText={setFinishedSearch}
                    />
                    {finishedSearch.length > 0 && (
                      <TouchableOpacity onPress={() => setFinishedSearch("")}>
                        <Feather name="x" size={14} color={colors.textMuted} />
                      </TouchableOpacity>
                    )}
                  </RNView>
                  <TouchableOpacity
                    style={[styles.secondaryButton, { borderColor: colors.border }]}
                    onPress={() => setShowFinishedResults(false)}
                  >
                    <Feather name="sliders" size={14} color={colors.textSecondary} />
                    <Text style={[styles.finishedButtonText, { color: colors.textSecondary }]}>
                      {t("changeFilters")}
                    </Text>
                  </TouchableOpacity>
                </RNView>
                <ScrollView style={{ maxHeight: 460 }}>
                  <DataTableWithPagination
                    headers={isSmallDevice ? SmallDeviceFinishedHeaders : finishedHeaders}
                    rows={filteredFinishedRows}
                    itemsPerPage={50}
                    useModal={false}
                    isRowsExpandable={true}
                    rowKeyValue="machineId"
                    nestedItemKeyValue="jobs"
                    nestedHeader={
                      isSmallDevice ? SmallDeviceFinishedNestedHeaders : finishedNestedHeaders
                    }
                    columnsToNotHaveThousandSeparator={["Works Order"]}
                    isDetail={false}
                    isEdit={false}
                    isDelete={false}
                    setModalVisible={() => {}}
                    setSelected={() => {}}
                    handleRowClick={() => {}}
                    handleDeleteRow={() => {}}
                  />
                </ScrollView>
              </>
            ) : machineListLoading ? (
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "12%" }}
              />
            ) : (
              <>
                <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
                  {t("dateRange")}
                </Text>
                <RNView style={styles.dateRow}>
                  <DateInputControl
                    title={t("from")}
                    value={startDate}
                    setDate={setStartDate}
                    width="48%"
                    defaultText={t("from")}
                    validRange={{ endDate: new Date() }}
                  />
                  <DateInputControl
                    title={t("to")}
                    value={endDate}
                    setDate={setEndDate}
                    width="48%"
                    defaultText={t("to")}
                    validRange={{ endDate: new Date() }}
                  />
                </RNView>

                <RNView style={styles.machineHeaderRow}>
                  <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
                    {t("lineMachine")}
                  </Text>
                  <TouchableOpacity onPress={toggleSelectAllMachines}>
                    <Text style={[styles.linkText, { color: colors.primary }]}>
                      {allMachinesSelected ? t("clearAll") : t("selectAll")}
                    </Text>
                  </TouchableOpacity>
                </RNView>
                <Text style={[styles.helperText, { color: colors.textMuted }]}>
                  {selectedMachineIds.size === 0
                    ? t("allMachinesHint")
                    : `${selectedMachineIds.size} selected`}
                </Text>

                <ScrollView style={{ maxHeight: 220 }}>
                  {machineList.map((machine) => {
                    const isSelected = selectedMachineIds.has(machine.machineId);
                    return (
                      <TouchableOpacity
                        key={machine.machineId}
                        style={styles.machineRow}
                        onPress={() => toggleMachineSelection(machine.machineId)}
                      >
                        <RNView
                          style={[
                            styles.checkbox,
                            {
                              borderColor: isSelected ? colors.primary : colors.border,
                              backgroundColor: isSelected ? colors.primary : "transparent",
                            },
                          ]}
                        >
                          {isSelected && <Feather name="check" size={12} color="white" />}
                        </RNView>
                        <Text style={[styles.machineName, { color: colors.text }]}>
                          {machine.machineName}
                        </Text>
                        <Text style={[styles.machineDescription, { color: colors.textMuted }]}>
                          {machine.machineDescription}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>

                <TouchableOpacity
                  style={[
                    styles.primaryButton,
                    { backgroundColor: colors.primary, opacity: finishedLoading ? 0.6 : 1 },
                  ]}
                  disabled={finishedLoading}
                  onPress={runFinishedReport}
                >
                  {finishedLoading ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <Text style={styles.primaryButtonText}>{t("runReport")}</Text>
                  )}
                </TouchableOpacity>
              </>
            )}
          </Pressable>
        </Pressable>
      </RNModal>

      <RNModal
        transparent={true}
        visible={importPreviewOpen}
        animationType="fade"
        onRequestClose={() => setImportPreviewOpen(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setImportPreviewOpen(false)}>
          <Pressable
            style={[
              styles.modalCard,
              { backgroundColor: colors.surface, borderColor: colors.border, width: "90%", maxWidth: 1420, maxHeight: "80%" },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={[styles.modalTitle, { color: colors.text, marginBottom: 4 }]}>
              {t("confirmIntouchImport")}
            </Text>
            <Text style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 12 }}>
              {selectedImportRows.size} of {importableRows.length} importable works order(s) selected to import.
              {importPreviewRows.some((r) => r.issue) &&
                ` ${importPreviewRows.filter((r) => r.issue).length} have an issue and can't be selected — see the Issue column.`}
              {alreadyInIntouchCount > 0 &&
                ` ${alreadyInIntouchCount} already exist in Intouch and were left unticked — tick one to import it again.`}
            </Text>

            <ScrollView horizontal={true} showsHorizontalScrollIndicator={true}>
              <View style={{ minWidth: 1420 }}>
                <View style={[styles.previewHeaderRow, { borderBottomColor: colors.borderSubtle }]}>
                  <TouchableOpacity
                    style={{ width: 28, alignItems: "flex-start" }}
                    onPress={toggleSelectAllImportRows}
                  >
                    <View
                      style={[
                        styles.checkbox,
                        {
                          borderColor: colors.primary,
                          backgroundColor: allImportableSelected ? colors.primary : colors.surface,
                        },
                      ]}
                    >
                      {allImportableSelected && <Feather name="check" size={12} color="#fff" />}
                    </View>
                  </TouchableOpacity>
                  {[
                    ["Works Order", 80],
                    ["In Intouch", 110],
                    ["Part Code", 130],
                    ["Description", 220],
                    ["Qty", 80],
                    ["Material", 110],
                    ["Machine", 150],
                    ["Tool Code", 90],
                    ["Cycle Time", 100],
                    ["Impressions", 90],
                    ["Issue", 220],
                  ].map(([label, width]) => (
                    <Text
                      key={label as string}
                      style={{ width: width as number, fontSize: 12, fontWeight: "700", color: colors.textSecondary }}
                    >
                      {label}
                    </Text>
                  ))}
                </View>
                <ScrollView style={{ maxHeight: 360 }} showsVerticalScrollIndicator={true}>
                  {importPreviewRows.map((row, index) => {
                    const isSelected = selectedImportRows.has(row.orderNumber);
                    return (
                      <View
                        key={`${row.orderNumber}-${index}`}
                        style={[
                          styles.previewDataRow,
                          { borderBottomColor: colors.borderSubtle, opacity: row.issue ? 0.5 : 1 },
                        ]}
                      >
                        <TouchableOpacity
                          style={{ width: 28, alignItems: "flex-start" }}
                          disabled={!!row.issue}
                          onPress={() => toggleImportRowSelection(row.orderNumber)}
                        >
                          <View
                            style={[
                              styles.checkbox,
                              {
                                borderColor: row.issue ? colors.textMuted : colors.primary,
                                backgroundColor: isSelected ? colors.primary : colors.surface,
                              },
                            ]}
                          >
                            {isSelected && <Feather name="check" size={12} color="#fff" />}
                          </View>
                        </TouchableOpacity>
                        <Text style={{ width: 80, fontSize: 13, color: colors.text }}>{row.orderNumber}</Text>
                        <RNView style={{ width: 110, flexDirection: "row", alignItems: "center", gap: 4 }}>
                          {row.intouchStatus ? (
                            <>
                              <Feather name="alert-circle" size={12} color="#D97706" />
                              <Text style={{ fontSize: 12, color: "#D97706" }} numberOfLines={1}>
                                {row.intouchStatus}
                              </Text>
                            </>
                          ) : (
                            <Text style={{ fontSize: 12, color: colors.textMuted }}>—</Text>
                          )}
                        </RNView>
                        <Text style={{ width: 130, fontSize: 13, color: colors.text }} numberOfLines={1}>{row.partCode}</Text>
                        <Text style={{ width: 220, fontSize: 13, color: colors.text }} numberOfLines={1}>{row.description}</Text>
                        <Text style={{ width: 80, fontSize: 13, color: colors.text }}>{row.orderQuantity}</Text>
                        <Text style={{ width: 110, fontSize: 13, color: colors.text }} numberOfLines={1}>{row.material}</Text>
                        <View style={{ width: 150, flexDirection: "row", alignItems: "center" }}>
                          <Feather
                            name={row.machineId ? "check-circle" : "alert-triangle"}
                            size={13}
                            color={row.machineId ? "#16A34A" : "#DC2626"}
                          />
                          <Text style={{ fontSize: 13, color: colors.text, marginLeft: 4 }} numberOfLines={1}>
                            {row.machineCode || "-"}
                          </Text>
                        </View>
                        <Text style={{ width: 90, fontSize: 13, color: colors.text }} numberOfLines={1}>{row.toolCode}</Text>
                        <Text style={{ width: 100, fontSize: 13, color: colors.text }}>{row.standardCycleTime}</Text>
                        <Text style={{ width: 90, fontSize: 13, color: colors.text }}>{row.impressions}</Text>
                        <Text style={{ width: 220, fontSize: 12, color: "#DC2626" }} numberOfLines={2}>
                          {row.issue || ""}
                        </Text>
                      </View>
                    );
                  })}
                  {importPreviewRows.length === 0 && (
                    <Text style={{ fontSize: 13, color: colors.textSecondary, paddingVertical: 12 }}>
                      No works orders are ready to import.
                    </Text>
                  )}
                </ScrollView>
              </View>
            </ScrollView>

            <View style={{ flexDirection: "row", justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
              <TouchableOpacity
                style={[styles.secondaryButton, { borderColor: colors.border, height: 40, paddingHorizontal: 16 }]}
                onPress={() => setImportPreviewOpen(false)}
              >
                <Text style={{ fontSize: 13, fontWeight: "600", color: colors.textSecondary }}>
                  {t("cancel")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 16,
                  borderRadius: 8,
                  backgroundColor: colors.primary,
                  opacity: importConfirmLoading || selectedImportRows.size === 0 ? 0.6 : 1,
                  flexDirection: "row",
                  alignItems: "center",
                }}
                disabled={importConfirmLoading || selectedImportRows.size === 0}
                onPress={handleConfirmImport}
              >
                {importConfirmLoading && (
                  <ActivityIndicator size="small" color="#fff" style={{ marginRight: 6 }} />
                )}
                <Text style={{ fontSize: 13, fontWeight: "700", color: "#fff" }}>
                  {t("confirmImport")}
                  {selectedImportRows.size > 0 ? ` (${selectedImportRows.size})` : ""}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </RNModal>

      <RNModal
        transparent={true}
        visible={importSummaryOpen}
        animationType="fade"
        onRequestClose={() => setImportSummaryOpen(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setImportSummaryOpen(false)}>
          <Pressable
            style={[
              styles.modalCard,
              { backgroundColor: colors.surface, borderColor: colors.border, maxWidth: 720 },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <RNView style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>{t("importSummary")}</Text>
              <TouchableOpacity onPress={() => setImportSummaryOpen(false)}>
                <Feather name="x" size={18} color={colors.textSecondary} />
              </TouchableOpacity>
            </RNView>

            <RNView style={styles.summaryCountsRow}>
              {([
                ["success", t("imported"), importedCount],
                ["warning", t("warnings"), importSummary.filter((e) => e.type === "warning").length],
                ["error", t("errors"), importSummary.filter((e) => e.type === "error").length],
              ] as Array<[SummaryEntryType, string, number]>).map(([type, label, count]) => (
                <RNView
                  key={type}
                  style={[
                    styles.summaryCountChip,
                    {
                      borderColor: count > 0 ? SUMMARY_STYLES[type].color : colors.border,
                      backgroundColor: colors.surfaceSubtle,
                    },
                  ]}
                >
                  <Feather
                    name={SUMMARY_STYLES[type].icon}
                    size={13}
                    color={count > 0 ? SUMMARY_STYLES[type].color : colors.textMuted}
                  />
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: "700",
                      color: count > 0 ? SUMMARY_STYLES[type].color : colors.textMuted,
                    }}
                  >
                    {count} {label}
                  </Text>
                </RNView>
              ))}
            </RNView>

            {importSummary.length === 0 ? (
              <Text style={{ fontSize: 13, color: colors.textSecondary, paddingVertical: 16 }}>
                {t("importAllGood")}
              </Text>
            ) : (
              <ScrollView style={{ maxHeight: 380 }}>
                {importSummary.map((entry, index) => (
                  <RNView
                    key={`${entry.orderNumber}-${index}`}
                    style={[styles.summaryEntry, { borderLeftColor: SUMMARY_STYLES[entry.type].color, backgroundColor: colors.surfaceSubtle }]}
                  >
                    <RNView style={styles.summaryEntryHeader}>
                      <Feather
                        name={SUMMARY_STYLES[entry.type].icon}
                        size={14}
                        color={SUMMARY_STYLES[entry.type].color}
                      />
                      <Text style={[styles.summaryOrderNumber, { color: colors.text }]}>
                        {entry.orderNumber ? `Works Order ${entry.orderNumber}` : t("importSummary")}
                      </Text>
                      <Text style={[styles.summaryStatusMessage, { color: SUMMARY_STYLES[entry.type].color }]}>
                        {entry.statusMessage}
                        {entry.statusCode ? ` (${entry.statusCode})` : ""}
                      </Text>
                    </RNView>
                    {!!entry.description && (
                      <Text style={[styles.summaryDescription, { color: colors.textSecondary }]}>
                        {entry.description}
                      </Text>
                    )}
                  </RNView>
                ))}
              </ScrollView>
            )}

            <TouchableOpacity
              style={[styles.primaryButton, { backgroundColor: colors.primary }]}
              onPress={() => setImportSummaryOpen(false)}
            >
              <Text style={styles.primaryButtonText}>{t("close")}</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </RNModal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },
  finishedButton: {
    height: 40,
    paddingHorizontal: 12,
    borderRadius: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
  },
  finishedButtonText: {
    fontSize: 12,
    fontWeight: "600",
  },
  expandLinkRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginRight: 2,
  },
  expandLink: {
    fontSize: 12,
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  expandLinkDivider: {
    fontSize: 12,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.35)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalCard: {
    width: "92%",
    maxWidth: 1200,
    borderRadius: 14,
    borderWidth: 1,
    padding: 20,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  modalActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
  },
  searchContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    height: 34,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    paddingVertical: 0,
    outlineStyle: "none",
  } as any,
  sectionLabel: {
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginTop: 10,
  },
  dateRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  machineHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 16,
  },
  linkText: {
    fontSize: 12,
    fontWeight: "600",
    marginTop: 10,
  },
  helperText: {
    fontSize: 11,
    marginTop: 2,
    marginBottom: 6,
  },
  machineRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    gap: 10,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    justifyContent: "center",
    alignItems: "center",
  },
  machineName: {
    fontSize: 13,
    fontWeight: "600",
    minWidth: 110,
  },
  machineDescription: {
    fontSize: 12,
    flex: 1,
  },
  primaryButton: {
    height: 44,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 18,
  },
  primaryButtonText: {
    color: "white",
    fontSize: 13,
    fontWeight: "700",
  },
  secondaryButton: {
    height: 34,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  previewHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 8,
    borderBottomWidth: 1,
  },
  previewDataRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  summaryCountsRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  summaryCountChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  summaryEntry: {
    borderLeftWidth: 3,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  summaryEntryHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  summaryOrderNumber: {
    fontSize: 13,
    fontWeight: "700",
  },
  summaryStatusMessage: {
    fontSize: 12,
    fontWeight: "600",
  },
  summaryDescription: {
    fontSize: 12,
    marginTop: 6,
    lineHeight: 17,
  },
});
